import ReactECharts from 'echarts-for-react';

import data from '../../data/firstHour.json';
import { useIsDark } from '../../useIsDark';

/**
 * blog26's figures — THREE interactive ECharts components reading ONE committed data file.
 *
 * CHART TYPE FIRST (RULES_BLOG_POST): each figure answers exactly one question, and the type is
 * chosen for that question before any styling.
 *
 *   DecayOfSpeedChart     what a delay costs                one measure over six delays -> vertical bar
 *   CloseBySpeedChart     what speed is worth in outcomes   one measure across five buckets -> vertical bar
 *   WhereRepliesLandChart how firms actually behave         a partition of one whole -> single stacked bar
 *
 * The three figures use three different types on purpose. The first two compare a value across
 * ordered buckets, so bars. The third is a partition of 100 percent, so a stacked bar: plotting four
 * independent bars against a 0 to 40 axis would hide that the four parts are one whole.
 *
 * Data: `../../data/firstHour.json`, the FE twin of the committed source-of-record CSV at
 * `playano-docs/raisy/projects/blog/assets/blog26/the_first_hour_decides.csv` (Rule 27). No number is
 * typed into this file. The series that are not plotted (`odds`, `practice_gap`, `booking`,
 * `persistence`) are the citations behind the prose.
 *
 * Theme: `dark` prop override, otherwise follows the live html class theme
 * (RULES_BLOG_POST Rule 29).
 */

type Row = {
  series: string;
  label: string;
  value: number;
  unit: string;
  display: string;
  source: string;
  confidence: string;
  note: string;
};

const ROWS = data.rows as Row[];
const rowsOf = (series: string): Row[] => ROWS.filter((r) => r.series === series);

/** The units these figures render. All three are percentages, so all three share one formatter. */
type Unit = 'pct_lift' | 'pct_close' | 'pct_of_firms';

const BRAND = {
  teal: '#0AB3BF',
  amber: '#F59E0B',
  orange: '#F97316',
  red: '#EF4444',
  slate: '#64748B',
  muted: '#64748B',
};

/**
 * The ONE formatter (Rule 21). Every tooltip, axis tick and bar label goes through it.
 *
 * `role` exists instead of a separate axis lambda so there is exactly one implementation of "a
 * number becomes a string". An axis grid may be coarser than a bar label because a gridline is a
 * scale rather than a value; that is a readability choice, and it is still made HERE. An
 * independently written axis formatter is the defect this rule names, because the same figure then
 * renders one number two ways.
 */
const formatFor = (unit: Unit, v: number, role: 'label' | 'axis' = 'label'): string => {
  const n = Number.isInteger(v) ? `${v}` : v.toFixed(1);
  // A lift is a change, so it carries its sign in both roles: an axis reading "100%" beside a bar
  // label reading "+391%" invites the reader to reconcile two different things.
  if (unit === 'pct_lift') return v === 0 ? '0%' : `+${n}%`;
  return `${n}%`;
};

const theme = (dark: boolean) =>
  dark
    ? {
        bg: '#0F172A',
        axis: '#E2E8F0',
        muted: '#94A3B8',
        grid: '#334155',
        card: 'bg-slate-900',
        border: 'border-slate-800',
        caption: 'text-slate-400',
      }
    : {
        bg: '#FFFFFF',
        axis: '#334155',
        muted: '#64748B',
        grid: '#334155',
        card: 'bg-white',
        border: 'border-slate-200',
        caption: 'text-slate-500',
      };

/** Shared axis furniture so the figures read as one series. */
const verticalBarOption = (
  rows: Row[],
  unit: Unit,
  colours: string[],
  dark: boolean,
  labelInside = false,
) => {
  const C = theme(dark);
  return {
    animationDuration: 600,
    backgroundColor: C.bg,
    grid: { left: 8, right: 16, top: 28, bottom: 8, containLabel: true },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(15,23,42,0.95)',
      borderColor: C.grid,
      textStyle: { color: '#E2E8F0', fontSize: 12 },
      formatter: (p: Array<{ name: string; value: number }>) =>
        `${p[0]?.name ?? ''}: <strong>${formatFor(unit, p[0]?.value ?? 0)}</strong>`,
    },
    xAxis: {
      type: 'category',
      data: rows.map((r) => r.label),
      axisLine: { lineStyle: { color: C.grid } },
      axisTick: { show: false },
      axisLabel: { color: C.axis, fontSize: 11, interval: 0, width: 86, overflow: 'break' },
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      splitLine: { lineStyle: { color: 'rgba(100,116,139,0.18)' } },
      axisLabel: { color: BRAND.muted, fontSize: 11, formatter: (v: number) => formatFor(unit, v, 'axis') },
    },
    series: [
      {
        type: 'bar',
        barMaxWidth: 62,
        data: rows.map((r, i) => ({
          value: r.value,
          itemStyle: { color: colours[i] ?? BRAND.teal, borderRadius: [4, 4, 0, 0] },
        })),
        label: {
          show: true,
          position: labelInside ? 'inside' : 'top',
          color: labelInside ? '#FFFFFF' : C.axis,
          fontSize: 12,
          fontWeight: 'bold',
          formatter: (p: { value: number }) => formatFor(unit, p.value),
        },
      },
    ],
  };
};

/** A teal to slate fade. The colour carries the same signal as the height, so the fade never
 *  contradicts the bar it is painted on. */
const FADE = ['#0AB3BF', '#3FC6CE', '#6FD3D9', '#9AA9B5', '#8A99A6', '#64748B'];

// ---------------------------------------------------------------------------------------------
// Figure 1 — what a delay costs
// ---------------------------------------------------------------------------------------------

export const DecayOfSpeedChart = ({
  height = 320,
  dark,
}: {
  height?: number;
  dark?: boolean;
}) => {
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;
  const C = theme(isDark);
  const rows = rowsOf('decay');
  const unit: Unit = 'pct_lift';

  return (
    <div className={`my-8 rounded-xl overflow-hidden border ${C.border} ${C.card} p-2`}>
      <ReactECharts
        option={verticalBarOption(rows, unit, rows.map((_, i) => FADE[i] ?? BRAND.slate), isDark)}
        style={{ height, width: '100%' }}
        opts={{ renderer: 'canvas' }}
      />
      <p className={`mt-2 text-xs ${C.caption}`}>
        Figure 1. What a delay costs. Conversion lift falls from about 391 percent for a contact made
        within a minute of a lead arriving, to about 160 percent at two minutes, 98 percent at three,
        62 percent at thirty, 36 percent at an hour, and 17 percent by the next day. The bars are
        spaced evenly and the delays are not, so read each bar against its label rather than along a
        timeline. The lift is measured against the study&apos;s own baseline. Source: Velocify,
        &quot;The Ultimate Contact Strategy&quot;, roughly 3.5 million leads across more than 400
        companies. A vendor dataset rather than a peer-reviewed trial, so medium confidence: the shape
        is confirmed by four independent datasets below, the exact percentages are not.
      </p>
    </div>
  );
};

// ---------------------------------------------------------------------------------------------
// Figure 2 — what speed is worth in outcomes
// ---------------------------------------------------------------------------------------------

export const CloseBySpeedChart = ({
  height = 320,
  dark,
}: {
  height?: number;
  dark?: boolean;
}) => {
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;
  const C = theme(isDark);
  const rows = rowsOf('close_by_speed');
  const unit: Unit = 'pct_close';

  return (
    <div className={`my-8 rounded-xl overflow-hidden border ${C.border} ${C.card} p-2`}>
      <ReactECharts
        option={verticalBarOption(rows, unit, rows.map((_, i) => FADE[i] ?? BRAND.slate), isDark)}
        style={{ height, width: '100%' }}
        opts={{ renderer: 'canvas' }}
      />
      <p className={`mt-2 text-xs ${C.caption}`}>
        Figure 2. What speed is worth at the end of the funnel, not just the start. Close rate runs
        32 percent when the first response goes out inside five minutes, 24 percent between five and
        thirty minutes, 18 percent up to an hour, 15 percent up to a day, and 12 percent after that.
        Everything else in this dataset is held as it was; the only variable being compared is when
        the response happened. Source: Optifai Lead Response Time Benchmark, 939 B2B SaaS companies,
        Q2 2025 to Q1 2026. A vendor benchmark, so medium confidence.
      </p>
    </div>
  );
};

// ---------------------------------------------------------------------------------------------
// Figure 3 — what firms actually do
// ---------------------------------------------------------------------------------------------

export const WhereRepliesLandChart = ({
  height = 190,
  dark,
}: {
  height?: number;
  dark?: boolean;
}) => {
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;
  const C = theme(isDark);
  const rows = rowsOf('practice');
  const unit: Unit = 'pct_of_firms';
  const colours = [BRAND.teal, BRAND.amber, BRAND.orange, BRAND.red];

  // A partition of one whole needs a stacked bar. Four separate bars against a 0 to 40 axis would
  // show four similar heights and hide that they are four parts of the same hundred enquiries.
  const option = {
    animationDuration: 600,
    backgroundColor: C.bg,
    grid: { left: 8, right: 16, top: 44, bottom: 8, containLabel: true },
    legend: {
      top: 0,
      textStyle: { color: C.muted, fontSize: 11 },
      data: rows.map((r) => r.label),
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(15,23,42,0.95)',
      borderColor: C.grid,
      textStyle: { color: '#E2E8F0', fontSize: 12 },
      formatter: (p: Array<{ seriesName: string; value: number }>) =>
        `${p[0]?.seriesName ?? ''}: <strong>${formatFor(unit, p[0]?.value ?? 0)}</strong>`,
    },
    xAxis: {
      type: 'value',
      max: 100,
      axisLine: { show: false },
      splitLine: { lineStyle: { color: 'rgba(100,116,139,0.18)' } },
      axisLabel: { color: BRAND.muted, fontSize: 11, formatter: (v: number) => formatFor(unit, v, 'axis') },
    },
    yAxis: {
      type: 'category',
      data: ['Every 100 inbound enquiries'],
      axisLine: { lineStyle: { color: C.grid } },
      axisTick: { show: false },
      axisLabel: { color: C.axis, fontSize: 11 },
    },
    series: rows.map((r, i) => ({
      name: r.label,
      type: 'bar',
      stack: 'one',
      data: [r.value],
      itemStyle: { color: colours[i] ?? BRAND.slate },
      label: {
        show: true,
        position: 'inside',
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: 'bold',
        formatter: () => formatFor(unit, r.value),
      },
    })),
  };

  return (
    <div className={`my-8 rounded-xl overflow-hidden border ${C.border} ${C.card} p-2`}>
      <ReactECharts option={option} style={{ height, width: '100%' }} opts={{ renderer: 'canvas' }} />
      <p className={`mt-2 text-xs ${C.caption}`}>
        Figure 3. What actually happens to an inbound enquiry, across 2,241 companies. 37 percent
        answered inside an hour, 16 percent sometime in the first day, 24 percent took longer than a
        day, and 23 percent never answered at all. The warmest colouring covers the 47 percent that
        replied after the window in which the odds in Figure 1 still exist. Source: Oldroyd, McElheran
        and Elkington, &quot;The Short Life of Online Sales Leads&quot;, Harvard Business Review, March
        2011, an audit of 2,241 US companies. Published and widely replicated, so high confidence.
        Newer audits are worse rather than better: a 2024 test of 1,000 SaaS companies found 63.5
        percent never replied at all.
      </p>
    </div>
  );
};
